import { useEffect, useRef } from "react";

/**
 * The end of a paged list: it asks for the next page as it scrolls into view,
 * so a long list just keeps going.
 *
 * The button stays as well. It is what a browser without IntersectionObserver
 * gets, and what somebody using a keyboard or a screen reader can press
 * without scrolling to a sentinel they cannot see. The observer is made again
 * whenever a page lands, so a page too short to push the end off-screen goes
 * straight on to the next rather than stalling.
 */
export function LoadMore({ more, loading, onMore }: { more: boolean; loading: boolean; onMore: () => void }) {
  const end = useRef<HTMLDivElement>(null);
  const ask = useRef(onMore);
  ask.current = onMore;

  useEffect(() => {
    const node = end.current;
    if (!more || loading || !node || typeof IntersectionObserver === "undefined") return;
    const watcher = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) ask.current();
      },
      { rootMargin: "0px 0px 600px 0px" },
    );
    watcher.observe(node);
    return () => watcher.disconnect();
  }, [more, loading]);

  if (!more) return null;
  return (
    <div className="load-more" ref={end}>
      <button type="button" className="button button-quiet" disabled={loading} onClick={onMore}>
        {loading ? "Loading…" : "Show more"}
      </button>
    </div>
  );
}
